<!--
 File: QuaffBlueContainer.vue
 Created: 2025-12-03 08:49:51
 Author: Val Liu <valuis0429@gmail.com>
 -----
 Last Modified: 2026-02-25 09:19:42
 Modified By: Val Liu <valuis0429@gmail.com>
-->

<template>
    <div class="quaff-app d-flex">
        <!-- 手機版遮罩：固定在畫面上，淡入淡出 -->
        <div
            class="sidebar-backdrop d-lg-none"
            :class="{ 'is-active': isSidebarShow }"
            @click="toggleSidebar(false)"
        ></div>

        <aside
            class="quaff-sidebar d-flex flex-column"
            :class="{ 'sidebar-show': isSidebarShow }"
        >
            <div class="quaff-brand d-flex align-items-center px-4">
                <slot name="brand">
                    <span
                        class="quaff-brand-logo d-inline-flex align-items-center justify-content-center mr-2"
                    >
                        Q
                    </span>
                    <div class="quaff-brand-text">
                        <div class="font-weight-bold">QUAFF AI</div>
                        <div class="small text-muted">Admin Console</div>
                    </div>
                </slot>
                <button
                    class="btn btn-link d-lg-none ml-auto text-white"
                    @click="toggleSidebar(false)"
                >
                    <i class="icon-close"></i>
                </button>
            </div>

            <div class="quaff-menu flex-fill overflow-auto mt-3">
                <slot name="nav">
                    <!-- 在外層用 <template v-slot:nav> 傳 SidebarNav 進來 -->
                </slot>
            </div>

            <div class="quaff-sidebar-footer px-4 py-3 small text-muted">
                <slot name="nav-footer"></slot>
            </div>
        </aside>

        <div class="quaff-main d-flex flex-column flex-fill">
            <header
                class="quaff-header d-flex align-items-center justify-content-between"
            >
                <div class="quaff-header-left d-flex align-items-center">
                    <button
                        class="btn btn-link d-lg-none mr-3 text-dark"
                        @click="toggleSidebar(true)"
                    >
                        <i class="icon-menu"></i>
                    </button>
                    <slot name="page-title">
                        <h1 class="h5 mb-0">
                            {{ currentTitle }}
                        </h1>
                    </slot>
                </div>
                <div class="quaff-header-right d-flex align-items-center">
                    <slot name="header-actions"> </slot>
                </div>
            </header>

            <main class="quaff-content flex-fill">
                <router-view />
            </main>
        </div>
    </div>
</template>

<style lang="scss" scoped>
// --- 顏色變數 ---
$tech-blue-darkest: #0a192f;
$tech-blue-dark: #0d1e37;
$tech-cyan-bright: #00e5ff;
$tech-cyan-glow: rgba(0, 229, 255, 0.6);
$text-color-white: #ffffff;
$text-color-gray: #b0c4de;
$sidebar-width: 260px; // 定義 Sidebar 寬度變數

// 手機版遮罩：固定在畫面上，只做淡入淡出
.sidebar-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1029; // 比 sidebar 略低，讓 sidebar 蓋在遮罩上
    opacity: 0;
    visibility: hidden;
    transition:
        opacity 0.3s ease-in-out,
        visibility 0.3s ease-in-out;

    &.is-active {
        opacity: 1;
        visibility: visible;
    }
}

.quaff-app {
    min-height: 100vh;
    background-color: #f3f4f6; // 主內容灰底
    overflow-x: hidden; // 防止水平捲軸
}

// 左側側邊欄美化
.quaff-sidebar {
    width: $sidebar-width; // 設定固定寬度
    min-width: $sidebar-width; // 防止被壓縮
    height: 100vh; // 固定高度
    position: fixed; // 固定定位
    top: 0;
    left: 0;
    z-index: 1030; // 確保在遮罩上方
    transition: transform 0.3s ease-in-out; // 加入滑動動畫
    transform: translateX(-100%); // 預設隱藏 (手機版)

    // 電腦版 (lg 以上) 永遠顯示
    @media (min-width: 992px) {
        transform: translateX(0) !important; // 強制顯示
        position: sticky; // 改為 sticky
        height: 100vh;
        z-index: 1; // 降低 z-index，避免蓋住 header 的陰影
    }

    // 手機版顯示狀態
    &.sidebar-show {
        transform: translateX(0);
    }

    color: $text-color-white;
    // 使用科技藍漸層背景，並疊加電路圖
    background:
        url("/public/background.jpg") center/cover no-repeat,
        linear-gradient(
            180deg,
            rgba($tech-blue-darkest, 0.95) 0%,
            rgba($tech-blue-dark, 0.9) 100%
        );
    background-blend-mode: overlay; // 讓電路圖與漸層混合
    border-right: 1px solid rgba($tech-cyan-bright, 0.2); // 增加右側發光邊框
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.2); // 加入陰影

    // Brand 區
    .quaff-brand {
        height: 64px; // 稍微增加高度
        padding: 0 24px;
        border-bottom: 1px solid rgba($tech-cyan-bright, 0.2);
        display: flex;
        align-items: center;
        flex-shrink: 0; // 防止被壓縮

        .quaff-brand-logo {
            width: 36px; // 加大 Logo
            height: 36px;
            border-radius: 50%;
            background: rgba($tech-blue-darkest, 0.8);
            border: 2px solid $tech-cyan-bright; // 發光邊框
            box-shadow: 0 0 10px $tech-cyan-glow; // 發光效果
            font-weight: 700;
            color: $tech-cyan-bright;
            font-size: 18px;
            margin-right: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .quaff-brand-text {
            .font-weight-bold {
                font-size: 1.2rem;
                color: $tech-cyan-bright; // 標題發光
                text-shadow: 0 0 8px $tech-cyan-glow;
            }
            .small.text-muted {
                color: $text-color-gray; // 副標題淺灰
            }
        }
    }

    // Menu 區
    .quaff-menu {
        padding: 20px 16px;
        overflow-y: auto; // 允許垂直捲動

        // 自定義捲軸樣式
        &::-webkit-scrollbar {
            width: 6px;
        }
        &::-webkit-scrollbar-thumb {
            background-color: rgba($tech-cyan-bright, 0.2);
            border-radius: 3px;
        }
        &::-webkit-scrollbar-track {
            background-color: transparent;
        }

        // 標題樣式
        ::v-deep .sidebar-nav-title {
            padding: 12px 0;
            font-size: 0.8rem;
            font-weight: 600;
            color: $text-color-gray;
            text-transform: uppercase;
            letter-spacing: 1px;
        }

        // 選單項目樣式
        ::v-deep .sidebar-nav-item {
            .sidebar-nav-link {
                color: $text-color-white;
                padding: 12px 16px;
                border-radius: 8px; // 圓角
                transition: all 0.3s;
                border-left: 4px solid transparent; // 左側指示條預留位
                display: flex;
                align-items: center;

                i {
                    color: $text-color-gray; // 圖示預設淺灰
                    transition: color 0.3s;
                    font-size: 1.1rem;
                }

                &:hover {
                    background-color: rgba($tech-cyan-bright, 0.1); // 懸停背景
                    color: $tech-cyan-bright;
                    i {
                        color: $tech-cyan-bright;
                    }
                }

                // 激活狀態
                &.active {
                    background: linear-gradient(
                        90deg,
                        rgba($tech-cyan-bright, 0.2) 0%,
                        transparent 100%
                    ); // 激活背景漸層
                    color: $tech-cyan-bright;
                    border-left-color: $tech-cyan-bright; // 左側發光指示條
                    box-shadow:
                        0 0 15px rgba($tech-cyan-bright, 0.1) inset,
                        -5px 0 10px rgba($tech-cyan-bright, 0.1); // 內部發光

                    i {
                        color: $tech-cyan-bright;
                        filter: drop-shadow(0 0 5px $tech-cyan-glow); // 圖示發光
                    }
                }
            }
        }
    }

    // 底部版權區
    .quaff-sidebar-footer {
        padding: 20px 24px;
        border-top: 1px solid rgba($tech-cyan-bright, 0.2);
        color: $text-color-gray;
        display: flex;
        flex-direction: column;
        gap: 6px;

        > .copyright {
            font-size: 0.75rem;
            color: #718096; /* 稍亮的灰色，保留品牌辨識度 */
            font-weight: 600;
            letter-spacing: 0.5px;
        }

        > .company-info {
            font-size: 0.65rem;
            color: #4a5568; /* 較暗的灰色，低調且合規 */
            line-height: 1.5;
        }

        > .company-info .company-contact {
            margin-left: 6px;
            color: #2b579a;
            text-decoration: none;
            font-weight: 600;
        }

        > .company-info .company-contact:hover {
            text-decoration: underline;
        }
    }
}

/* 右側主畫面 */
.quaff-main {
    background-color: #f3f4f6;
    height: 100vh; // 🔸唯一高度調整：固定滿一個 viewport
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    position: relative;

    @media (min-width: 992px) {
        width: auto; // 保持原本 flex-fill 行為
        flex: 1; // 佔滿剩餘空間
    }
}

.quaff-header {
    height: 64px;
    padding: 0 1.5rem;
    background-color: #ffffff;
    border-bottom: 1px solid #e5e7eb;
    box-shadow: 0 4px 12px rgba(13, 30, 55, 0.1);
    flex-shrink: 0; // 防止 header 被壓縮
    position: relative;
    z-index: 2;
}

.quaff-content {
    flex: 1; // 佔滿 header 下方剩餘空間
    overflow-y: auto; // 這裡會出 scrollbar
    overflow-x: hidden;
    position: relative;
    z-index: 1;
}
</style>

<script>
export default {
    name: "QuaffBlueContainer",
    data() {
        return {
            isSidebarShow: false, // 控制手機版 Sidebar 顯示狀態
        };
    },
    computed: {
        currentTitle() {
            // 讓你可以在 route.meta.title 設定標題
            return (this.$route.meta && this.$route.meta.title) || "";
        },
    },
    methods: {
        toggleSidebar(show) {
            this.isSidebarShow = show;
        },
    },
    watch: {
        $route() {
            // 路由切換時自動關閉手機版 Sidebar
            this.isSidebarShow = false;
        },
    },
};
</script>
